import { useCallback, useRef } from 'react';
import { TURNSTILE_BYPASS_EMAIL_DOMAINS } from '@trulyinbox/types';
import { env } from '@/lib/env';

const TURNSTILE_SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

/** Narrow slice of Cloudflare's `window.turnstile` global this hook calls. */
interface TurnstileApi {
  render: (
    container: HTMLElement,
    options: { sitekey: string; size?: 'normal' | 'flexible' | 'compact' },
  ) => string | undefined;
  getResponse: (widgetId: string) => string | undefined;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

// Memoized module-level promise so the script tag is injected once however
// many forms mount the hook. Cleared on failure so a later mount can retry.
let turnstileScriptPromise: Promise<TurnstileApi> | null = null;

function loadTurnstileScript(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (turnstileScriptPromise) return turnstileScriptPromise;
  turnstileScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    const fail = (message: string) => {
      turnstileScriptPromise = null;
      script.remove();
      reject(new Error(message));
    };
    script.onload = () => {
      if (window.turnstile) resolve(window.turnstile);
      else fail('Cloudflare Turnstile script loaded without window.turnstile');
    };
    script.onerror = () => fail('Failed to load Cloudflare Turnstile script');
    document.head.appendChild(script);
  });
  return turnstileScriptPromise;
}

/**
 * Exact-matches the email's domain against `TURNSTILE_BYPASS_EMAIL_DOMAINS`
 * — mirrors `TurnstileGuard.isBypassedEmailDomain` on the API so the widget
 * disappears exactly when the server would have skipped the check anyway.
 * A suffix/substring match would also let `evil-saleshandy.com` or
 * `saleshandy.com.evil.com` through.
 */
function isBypassedEmailDomain(email: string): boolean {
  const domain = email.toLowerCase().split('@')[1];
  return domain !== undefined && (TURNSTILE_BYPASS_EMAIL_DOMAINS as readonly string[]).includes(domain);
}

/**
 * Cloudflare Turnstile widget for the public sign-in forms. Attach
 * `containerRef` to an empty `<div>`; the widget renders into it once the
 * script loads and is removed when the div unmounts. Read the single-use token
 * with `getToken()` at submit time and call `reset()` after a failed request so
 * the user gets a fresh token for the retry.
 *
 * No-ops entirely when `env.turnstileSiteKey` is unset (local dev/CI): nothing
 * renders and `getToken()` returns `undefined`, so no `captchaToken` is sent —
 * matching an api with no `CLOUDFLARE_TURNSTILE_SECRET_KEY`, which skips the check.
 *
 * Pass the form's current `email` (as it's typed): once it matches
 * `TURNSTILE_BYPASS_EMAIL_DOMAINS`, `enabled` flips false and the caller's
 * `{captcha.enabled && <div ref={captcha.containerRef} />}` unmounts the
 * widget — the API bypasses the same domains, so there is nothing for the
 * user to solve. Purely cosmetic: the API never trusts this hook's verdict,
 * it re-derives its own from the same shared domain list.
 */
export function useTurnstile(email = '') {
  const siteKey = env.turnstileSiteKey;
  const widgetIdRef = useRef<string | null>(null);

  // Stable callback ref with a React 19 cleanup return, so the widget's
  // lifetime tracks the container's — a form that toggles the container (the
  // login card's password mode) gets a fresh widget each time it reappears.
  const containerRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node || !siteKey) return;
      let cancelled = false;
      let rendered: { api: TurnstileApi; widgetId: string } | null = null;

      loadTurnstileScript()
        .then((api) => {
          if (cancelled) return;
          const widgetId = api.render(node, { sitekey: siteKey, size: 'flexible' });
          if (widgetId === undefined) return;
          rendered = { api, widgetId };
          widgetIdRef.current = widgetId;
        })
        .catch((err: unknown) => {
          // Silent in the UI: with no widget there's no token, and the api's
          // own 403 is the message the user sees on submit.
          console.warn('useTurnstile: failed to load Cloudflare Turnstile', err);
        });

      return () => {
        cancelled = true;
        if (!rendered) return;
        rendered.api.remove(rendered.widgetId);
        if (widgetIdRef.current === rendered.widgetId) widgetIdRef.current = null;
      };
    },
    [siteKey],
  );

  function getToken(): string | undefined {
    const widgetId = widgetIdRef.current;
    if (widgetId === null) return undefined;
    return window.turnstile?.getResponse(widgetId) || undefined;
  }

  function reset(): void {
    const widgetId = widgetIdRef.current;
    if (widgetId !== null) window.turnstile?.reset(widgetId);
  }

  return {
    enabled: Boolean(siteKey) && !isBypassedEmailDomain(email),
    containerRef,
    getToken,
    reset,
  };
}
